import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { Container } from "@/components/site/Container";
import { canonical } from "@/lib/seo";
import { LOCATIONS, LOCATION_GROUPS } from "@/lib/locations";
import { listAllPublishedForSitemap } from "@/lib/blog.functions";

const TITLE = "Site Map | Durafoam Roofing";
const DESCRIPTION =
  "Browse all pages on the Durafoam Roofing website — services, cost guides, service areas, and roofing resources for Phoenix Valley homeowners.";

const MAIN_PAGES = [
  { label: "Home", href: "/" },
  { label: "About Us", href: "/about-us" },
  { label: "Contact Us", href: "/contact-us" },
  { label: "Free Roof Inspection", href: "/free-roof-inspection-phoenix" },
  { label: "Reviews", href: "/reviews" },
  { label: "Gallery", href: "/gallery" },
  { label: "Cost Solutions", href: "/financing" },
  { label: "Warranty", href: "/warranty" },
];

const SERVICES = [
  { slug: "foam-roofing", label: "Foam Roofing" },
  { slug: "foam-roof-coating-recoating", label: "Foam Roof Coating & Recoating" },
  { slug: "foam-roof-repair-phoenix", label: "Foam Roof Repair" },
  { slug: "roof-leak-repair", label: "Roof Leak Repair" },
  { slug: "roof-repair", label: "Roof Repair" },
  { slug: "emergency-roof-leak-repair", label: "Emergency Roof Leak Repair" },
  { slug: "tile-roofing", label: "Tile Roofing" },
  { slug: "shingle-roofing", label: "Shingle Roofing" },
  { slug: "flat-roof-repair", label: "Flat Roof Repair" },
  { slug: "commercial-roofing", label: "Commercial Roofing" },
  { slug: "hoa-roofing", label: "HOA Roofing" },
  { slug: "apartment-roofing", label: "Apartment Roofing" },
  { slug: "skylight-repair", label: "Skylight Repair" },
  { slug: "roof-maintenance", label: "Roof Maintenance" },
  { slug: "roof-inspections-for-realtors", label: "Roof Inspections for Realtors" },
  { slug: "free-roof-inspection-phoenix", label: "Free Roof Inspection" },
  { slug: "hail-damage-roof-inspection-phoenix", label: "Hail Damage Roof Inspection" },
];

const COSTS = [
  { slug: "foam-roof-recoating-cost-in-phoenix", label: "Foam Roof Recoating Cost" },
  { slug: "foam-roof-replacement-cost-phoenix", label: "Foam Roof Replacement Cost" },
  { slug: "tile-roof-replacement-cost-in-phoenix", label: "Tile Roof Replacement Cost" },
  { slug: "shingle-roof-cost-phoenix", label: "Shingle Roof Cost" },
  { slug: "roof-repair-cost-phoenix", label: "Roof Repair Cost" },
  { slug: "flat-roof-repair-cost-phoenix", label: "Flat Roof Repair Cost" },
  { slug: "commercial-roofing-cost-phoenix", label: "Commercial Roofing Cost" },
  { slug: "roof-inspection-cost-phoenix", label: "Roof Inspection Cost" },
  { slug: "skylight-repair-cost-phoenix", label: "Skylight Repair Cost" },
];

const LEGAL = [
  { label: "Privacy Policy", href: "/privacy-policy" },
  { label: "Terms of Service", href: "/terms" },
];

export const Route = createFileRoute("/sitemap")({
  head: () => {
    const url = canonical("/sitemap");
    return {
      meta: [
        { title: TITLE },
        { name: "description", content: DESCRIPTION },
        { property: "og:title", content: TITLE },
        { property: "og:description", content: DESCRIPTION },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
        { name: "twitter:card", content: "summary_large_image" },
      ],
      links: [{ rel: "canonical", href: url }],
    };
  },
  component: SitemapPage,
});

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h2 className="text-xl font-bold text-primary">{title}</h2>
      <ul className="mt-4 grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-2 lg:grid-cols-3">
        {children}
      </ul>
    </section>
  );
}

function Item({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <li>
      <Link to={to} className="text-foreground hover:text-accent hover:underline">
        {children}
      </Link>
    </li>
  );
}

function SitemapPage() {
  const listBlog = useServerFn(listAllPublishedForSitemap);
  const blogQuery = useQuery({ queryKey: ["sitemap-blog"], queryFn: () => listBlog() });

  const locByGroup = LOCATION_GROUPS.map((g) => ({
    label: g.label,
    items: g.slugs.map((slug) => LOCATIONS[slug]).filter(Boolean),
  }));

  return (
    <Container className="py-12 lg:py-16">
      <h1 className="text-3xl font-bold text-primary">Site map</h1>
      <p className="mt-2 text-sm text-[var(--muted-foreground)]">
        Every page on the Durafoam Roofing website.
      </p>

      <div className="mt-10 space-y-10">
        <Section title="Main pages">
          {MAIN_PAGES.map((p) => <Item key={p.href} to={p.href}>{p.label}</Item>)}
        </Section>

        <Section title="Roofing services">
          {SERVICES.map((s) => <Item key={s.slug} to={`/${s.slug}`}>{s.label}</Item>)}
        </Section>

        <Section title="Roofing cost guides">
          {COSTS.map((s) => <Item key={s.slug} to={`/${s.slug}`}>{s.label}</Item>)}
        </Section>

        {locByGroup.map((g) => (
          <Section key={g.label} title={g.label}>
            {g.items.map((l) => <Item key={l.slug} to={l.path}>{l.name}</Item>)}
          </Section>
        ))}

        <section>
          <h2 className="text-xl font-bold text-primary">Blog</h2>
          <p className="mt-2 text-sm">
            <Link to="/blog" className="text-accent hover:underline">All blog posts →</Link>
          </p>
          <ul className="mt-4 grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
            {(blogQuery.data ?? []).map((p) => {
              const label = p.path
                .replace(/^\/+|\/+$/g, "")
                .split("/")
                .pop()!
                .replace(/-/g, " ")
                .replace(/\b\w/g, (c) => c.toUpperCase());
              return (
                <li key={p.path}>
                  <a href={p.path} className="text-foreground hover:text-accent hover:underline">
                    {label}
                  </a>
                </li>
              );
            })}
          </ul>
        </section>

        <Section title="Legal">
          {LEGAL.map((p) => <Item key={p.href} to={p.href}>{p.label}</Item>)}
        </Section>
      </div>
    </Container>
  );
}
